/*
 * Product gallery: thumbnails, previous and next, keyboard, a zoom lightbox, and switching to a variant's picture when the shopper chooses it.
 * The markup works without this script (a scroll-snap strip with thumbnail links); this only adds to it.
 * The pure functions at the top are exported for the unit tests in tests/unit.
 */
(() => {
  /** The slide a step lands on, staying inside the gallery (no wrap-around, so the ends are clear to a keyboard user). */
  const nextIndex = (current, delta, length) => Math.min(Math.max(current + delta, 0), Math.max(length - 1, 0));

  /** The index of the slide showing a media id, or -1. */
  const indexForMedia = (mediaIds, mediaId) => mediaIds.findIndex((id) => String(id) === String(mediaId));

  /** The slide nearest the middle of the viewport, from each slide's left edge and width. */
  const nearestIndex = (scrollLeft, slideWidth, count) => (slideWidth > 0 ? Math.min(Math.max(Math.round(scrollLeft / slideWidth), 0), Math.max(count - 1, 0)) : 0);

  /** Where a zoomed picture is centred, as percentages of the picture, from a click inside its box. */
  const zoomOrigin = (clientX, clientY, rect) => {
    if (!rect || rect.width <= 0 || rect.height <= 0) return { x: 50, y: 50 };
    const clamp = (value) => Math.min(Math.max(value, 0), 100);
    return { x: clamp(((clientX - rect.left) / rect.width) * 100), y: clamp(((clientY - rect.top) / rect.height) * 100) };
  };

  /** A larger version of a Shopify image address (its width parameter replaced), leaving any other address alone. */
  const largeSource = (src, width = 2000) => (src ? String(src).replace(/([?&]width=)\d+/, `$1${width}`) : '');

  const api = { nextIndex, indexForMedia, nearestIndex, zoomOrigin, largeSource };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined') return;
  window.ShopStarGallery = api;

  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setup = (gallery) => {
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const steps = [...gallery.querySelectorAll('[data-gallery-step]')];
    const status = gallery.querySelector('[data-gallery-status]');
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    if (!viewport || slides.length === 0) return;
    gallery.classList.add('is-enhanced');
    const mediaIds = slides.map((slide) => slide.dataset.mediaId);
    let current = 0;

    const announce = () => { if (status) status.textContent = `${current + 1} / ${slides.length}`; };
    const mark = (index) => {
      current = index;
      thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
      });
      const active = thumbs[index];
      if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' });
      steps.forEach((button) => {
        const delta = Number(button.dataset.galleryStep);
        button.disabled = nextIndex(index, delta, slides.length) === index;
      });
      announce();
    };

    const show = (index) => {
      const target = slides[nextIndex(index, 0, slides.length)];
      viewport.scrollTo({ left: target.offsetLeft - viewport.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
      mark(nextIndex(index, 0, slides.length));
    };

    steps.forEach((button) => { button.hidden = false; button.addEventListener('click', () => show(nextIndex(current, Number(button.dataset.galleryStep), slides.length))); });
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', (event) => { event.preventDefault(); show(index); }));

    let frame = 0;
    viewport.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const index = nearestIndex(viewport.scrollLeft, slides[0].getBoundingClientRect().width, slides.length);
        if (index !== current) mark(index);
      });
    }, { passive: true });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); show(nextIndex(current, 1, slides.length)); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(nextIndex(current, -1, slides.length)); }
      if (event.key === 'Home') { event.preventDefault(); show(0); }
      if (event.key === 'End') { event.preventDefault(); show(slides.length - 1); }
    });

    // Choosing a size or colour shows that variant's picture.
    const scope = gallery.closest('[data-product-form-scope]') || document;
    scope.addEventListener('shopstar:variant_change', (event) => {
      const mediaId = event.detail?.variant?.featured_media?.id;
      const index = mediaId ? indexForMedia(mediaIds, mediaId) : -1;
      if (index >= 0) show(index);
    });

    // Lightbox: switched off (no zoom on the product page)
    mark(0);
    return;
    if (!lightbox || typeof lightbox.showModal !== 'function') return;
    const image = lightbox.querySelector('[data-gallery-lightbox-image]');
    const stage = lightbox.querySelector('[data-gallery-stage]');
    let zoomed = false;
    const imageSlides = slides.map((slide, index) => ({ slide, index })).filter(({ slide }) => slide.dataset.mediaType === 'image');
    const zoomButtons = gallery.querySelectorAll('[data-gallery-zoom]');
    zoomButtons.forEach((button) => { button.hidden = false; });

    const setZoom = (on, event) => {
      zoomed = on;
      lightbox.classList.toggle('is-zoomed', on);
      if (on && event) {
        const origin = zoomOrigin(event.clientX, event.clientY, image.getBoundingClientRect());
        image.style.transformOrigin = `${origin.x}% ${origin.y}%`;
      } else if (!on) image.style.transformOrigin = '';
    };
    const showInLightbox = (slideIndex) => {
      const position = imageSlides.findIndex((item) => item.index === slideIndex);
      if (position < 0) return;
      const source = imageSlides[position].slide.querySelector('img');
      if (!source) return;
      image.src = largeSource(source.currentSrc || source.src);
      image.alt = source.alt || '';
      lightbox.dataset.position = String(position);
      setZoom(false);
      show(slideIndex);
    };
    const stepLightbox = (delta) => {
      const position = Number(lightbox.dataset.position || 0);
      const next = nextIndex(position, delta, imageSlides.length);
      if (next !== position) showInLightbox(imageSlides[next].index);
    };

    gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gallery-zoom], .gallery__slide img');
      if (!trigger) return;
      const slide = trigger.closest('[data-gallery-slide]');
      if (!slide || slide.dataset.mediaType !== 'image') return;
      lightbox.showModal();
      showInLightbox(slides.indexOf(slide));
    });
    lightbox.querySelector('[data-gallery-close]')?.addEventListener('click', () => lightbox.close());
    lightbox.querySelectorAll('[data-gallery-lightbox-step]').forEach((button) => button.addEventListener('click', () => stepLightbox(Number(button.dataset.galleryLightboxStep))));
    stage.addEventListener('click', (event) => setZoom(!zoomed, event));
    lightbox.addEventListener('click', (event) => { if (event.target === lightbox) lightbox.close(); });
    lightbox.addEventListener('close', () => setZoom(false));
    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') stepLightbox(1);
      if (event.key === 'ArrowLeft') stepLightbox(-1);
    });
    mark(0);
  };

  const init = () => document.querySelectorAll('[data-gallery]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
